/**
 * Compact report view-state encoding for URL query params.
 *
 * - `cols`: hex bitmask of visible columns over a stable ordered id list, plus `.<count>`
 *   (the number of ids when it was written) when the last columns are hidden
 * - `sort`: `columnId.asc` | `columnId.desc`
 */

/**
 * Encode visible columns as a short hex bitmask.
 * Returns null when all columns are visible (caller should omit the param).
 *
 * A bare mask can't tell a hidden last column from one added after the link was made (both
 * sit past its top set bit), so when the last column is hidden the id count is appended:
 * `3f.8` hides ids 6 and 7 of 8, where `3f` alone shows them.
 */
export function encodeCols(
  allIds: readonly string[],
  visibleIds: readonly string[]
): string | null {
  if (allIds.length === 0) return null;
  const visible = new Set(visibleIds);
  const allVisible = allIds.every((id) => visible.has(id));
  if (allVisible) return null;

  let bits = 0n;
  for (let i = 0; i < allIds.length; i++) {
    if (visible.has(allIds[i]!)) {
      bits |= 1n << BigInt(i);
    }
  }
  const hex = bits.toString(16);
  return visible.has(allIds[allIds.length - 1]!) ? hex : `${hex}.${allIds.length}`;
}

/**
 * Parse a `cols` value: the mask, and how many ids it covers. Without a `.<count>` suffix the
 * mask covers ids up to its top set bit. Null when missing or malformed.
 */
export function parseColsParam(
  colsParam: string | null | undefined
): { bits: bigint; count: number } | null {
  if (!colsParam) return null;
  const match = /^([0-9a-fA-F]+)(?:\.(\d{1,4}))?$/.exec(colsParam);
  if (!match) return null;
  let bits: bigint;
  try {
    bits = BigInt(`0x${match[1]}`);
  } catch {
    return null;
  }
  const width = bits === 0n ? 0 : bits.toString(2).length;
  const count = match[2] == null ? width : Math.max(Number(match[2]), width);
  return { bits, count };
}

/**
 * Decode a cols param into visible column ids.
 * Falls back to all ids on invalid/empty/zero-visible results.
 *
 * Ids past the mask's count were added after it was written, so they show by default
 * without breaking existing visibility for older columns.
 */
export function decodeCols(
  allIds: readonly string[],
  colsParam: string | null | undefined
): string[] {
  if (allIds.length === 0) return [];
  const parsed = parseColsParam(colsParam);
  if (!parsed) return [...allIds];
  const { bits, count } = parsed;
  const visible = allIds.filter(
    (_, i) => i >= count || (bits & (1n << BigInt(i))) !== 0n
  );
  return visible.length > 0 ? visible : [...allIds];
}

export type SortDir = 'asc' | 'desc';

export interface DecodedSort {
  id: string;
  dir: SortDir;
}

/** Encode sort as `columnId.asc` or `columnId.desc`. */
export function encodeSort(id: string, dir: SortDir): string {
  return `${id}.${dir}`;
}

/**
 * Decode `sort` query param. Returns default when missing/invalid.
 */
export function decodeSort(
  param: string | null | undefined,
  allowedIds: readonly string[],
  defaultSort: DecodedSort
): DecodedSort {
  if (!param) return defaultSort;
  const dot = param.lastIndexOf('.');
  if (dot <= 0) return defaultSort;
  const id = param.slice(0, dot);
  const dirRaw = param.slice(dot + 1);
  if (dirRaw !== 'asc' && dirRaw !== 'desc') return defaultSort;
  if (!allowedIds.includes(id)) return defaultSort;
  return { id, dir: dirRaw };
}

/** True when sort matches the report default (caller should omit from URL). */
export function isDefaultSort(
  sort: DecodedSort,
  defaultSort: DecodedSort
): boolean {
  return sort.id === defaultSort.id && sort.dir === defaultSort.dir;
}

/** Apply cols + sort onto URLSearchParams (omits defaults). */
export function applyViewStateToSearchParams(
  params: URLSearchParams,
  options: {
    allColumnIds: readonly string[];
    visibleColumnIds: readonly string[];
    sort: DecodedSort;
    defaultSort: DecodedSort;
  }
): void {
  const cols = encodeCols(options.allColumnIds, options.visibleColumnIds);
  if (cols) params.set('cols', cols);
  else params.delete('cols');

  if (!isDefaultSort(options.sort, options.defaultSort)) {
    params.set('sort', encodeSort(options.sort.id, options.sort.dir));
  } else {
    params.delete('sort');
  }
}
